import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, CheckCircle2, Save, Trash2, ShieldCheck, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FrequencyField = ({ field, value, onChange }) => {
  const setRow = (row, v) => onChange({ ...(value || {}), [row]: v });
  return (
    <div className="mt-2 rounded-xl p-3" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}>
      <div className="freq-row freq-header"><div>Síntoma</div><div className="freq-cell">Nunca</div><div className="freq-cell">A veces</div><div className="freq-cell">Siempre</div></div>
      {field.rows.map((row) => (
        <div key={row} className="freq-row">
          <div>{row}</div>
          {["nunca", "a_veces", "siempre"].map((opt) => (
            <div key={opt} className="freq-cell">
              <input type="radio" name={`${field.id}_${row}`} checked={(value?.[row] || "") === opt} onChange={() => setRow(row, opt)} data-testid={`freq-${field.id}-${row}-${opt}`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const Frequency3ColField = ({ field, value, onChange }) => {  const cols = field.columns || [];
  const setRow = (row, v) => onChange({ ...(value || {}), [row]: v });
  return (
    <div className="mt-2 rounded-xl p-3 overflow-x-auto" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}>
      <div className="freq-row freq-header" style={{ gridTemplateColumns: `1.4fr repeat(${cols.length}, 1fr)` }}>
        <div>Asignatura</div>
        {cols.map((c) => (<div key={c} className="freq-cell" style={{ fontSize: "0.78rem" }}>{c}</div>))}
      </div>
      {field.rows.map((row) => (
        <div key={row} className="freq-row" style={{ gridTemplateColumns: `1.4fr repeat(${cols.length}, 1fr)` }}>
          <div>{row}</div>
          {cols.map((c) => (
            <div key={c} className="freq-cell">
              <input
                type="radio"
                name={`${field.id}_${row}`}
                checked={(value?.[row] || "") === c}
                onChange={() => setRow(row, c)}
                style={{ accentColor: "var(--sage-500)" }}
                data-testid={`freq3-${field.id}-${row.substring(0, 12).replace(/\s+/g, "_")}`}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const Frequency4ColField = ({ field, value, onChange }) => {
  const cols = field.columns || ["0", "1", "2", "3"];
  const setRow = (row, v) => onChange({ ...(value || {}), [row]: v });
  return (
    <div className="mt-2 rounded-xl p-3 overflow-x-auto" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }} data-testid={`field-${field.id}`}>
      <div className="freq-row freq-header" style={{ gridTemplateColumns: `2.4fr repeat(${cols.length}, 1fr)`, minWidth: 600 }}>
        <div>Síntoma</div>
        {cols.map((c) => (<div key={c} className="freq-cell" style={{ fontSize: "0.72rem", textAlign: "center" }}>{c}</div>))}
      </div>
      {field.rows.map((row, idx) => (
        <div key={row} className="freq-row" style={{ gridTemplateColumns: `2.4fr repeat(${cols.length}, 1fr)`, minWidth: 600, alignItems: "center" }}>
          <div style={{ fontSize: "0.86rem", lineHeight: 1.35, padding: "8px 4px" }}>{row}</div>
          {[0, 1, 2, 3].map((score) => (
            <div key={score} className="freq-cell" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <input
                type="radio"
                name={`${field.id}_${idx}`}
                checked={(value?.[row] ?? -1) === score}
                onChange={() => setRow(row, score)}
                style={{ accentColor: "var(--sage-500)", width: 16, height: 16 }}
                data-testid={`freq4-${field.id}-${idx}-${score}`}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};


const FamilyMembersField = ({ field, value, onChange }) => {
  const setCell = (row, col, v) => {
    const cur = value || {};
    onChange({ ...cur, [row]: { ...(cur[row] || {}), [col]: v } });
  };
  return (
    <div className="mt-2 rounded-xl p-3 overflow-x-auto" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}>
      <div className="disease-row disease-header" style={{ gridTemplateColumns: "1fr 2fr 1.2fr" }}>
        <div>Miembro</div>
        <div style={{ paddingLeft: 8 }}>Nombre</div>
        <div style={{ paddingLeft: 8 }}>Fecha de nacimiento</div>
      </div>
      {field.rows.map((row) => (
        <div key={row} className="disease-row" style={{ gridTemplateColumns: "1fr 2fr 1.2fr", alignItems: "center" }}>
          <div style={{ fontWeight: 500 }}>{row}</div>
          <div style={{ padding: "4px 6px" }}>
            <input
              type="text"
              className="field-input"
              value={value?.[row]?.nombre || ""}
              onChange={(e) => setCell(row, "nombre", e.target.value)}
              placeholder="Nombre"
              data-testid={`family-${row.replace(/\s+/g, "_")}-nombre`}
              style={{ padding: "0.45rem 0.7rem", fontSize: "0.9rem" }}
            />
          </div>
          <div style={{ padding: "4px 6px" }}>
            <input
              type="date"
              className="field-input"
              value={value?.[row]?.fecha || ""}
              onChange={(e) => setCell(row, "fecha", e.target.value)}
              data-testid={`family-${row.replace(/\s+/g, "_")}-fecha`}
              style={{ padding: "0.45rem 0.7rem", fontSize: "0.9rem" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const DiseaseField = ({ field, value, onChange }) => {
  const setRow = (row, col, v) => {
    const cur = value || {};
    onChange({ ...cur, [row]: { ...(cur[row] || {}), [col]: v } });
  };
  return (
    <div className="mt-2 rounded-xl p-3" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}>
      <div className="disease-row disease-header"><div>Enfermedad</div><div className="freq-cell">Paciente</div><div className="freq-cell">Familia</div></div>
      {field.rows.map((row) => (
        <div key={row} className="disease-row">
          <div>{row}</div>
          <div className="freq-cell"><input type="checkbox" checked={!!value?.[row]?.paciente} onChange={(e) => setRow(row, "paciente", e.target.checked)} data-testid={`disease-${row}-paciente`} /></div>
          <div className="freq-cell"><input type="checkbox" checked={!!value?.[row]?.familia} onChange={(e) => setRow(row, "familia", e.target.checked)} data-testid={`disease-${row}-familia`} /></div>
        </div>
      ))}
    </div>
  );
};

const CheckboxListField = ({ field, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((v) => v !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="mt-2 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 gap-2" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }} data-testid={`field-${field.id}`}>
      {field.options.map((opt) => (
        <label key={opt} className="flex items-start gap-2 cursor-pointer font-sans-body text-sm py-1.5 px-2 rounded-lg transition-colors hover:bg-[color:var(--sage-50)]">
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={() => toggle(opt)}
            style={{ accentColor: "var(--sage-500)", marginTop: "3px" }}
            data-testid={`field-${field.id}-${opt.substring(0, 20).replace(/\s+/g, "_")}`}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
};

const ProfessionalsTableField = ({ field, value, onChange }) => {
  const setCell = (row, col, v) => {
    const cur = value || {};
    onChange({ ...cur, [row]: { ...(cur[row] || {}), [col]: v } });
  };
  return (
    <div className="mt-2 rounded-xl p-3 overflow-x-auto" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }} data-testid={`field-${field.id}`}>
      <div className="disease-row disease-header" style={{ gridTemplateColumns: "1.4fr 0.9fr 1.1fr 2fr 2fr", minWidth: 760 }}>
        <div>Profesional</div>
        <div className="freq-cell">Consultado</div>
        <div style={{ paddingLeft: 8 }}>Fecha</div>
        <div style={{ paddingLeft: 8 }}>Nombre y centro</div>
        <div style={{ paddingLeft: 8 }}>Resultados / recomendaciones</div>
      </div>
      {field.rows.map((row) => {
        const safeId = row.substring(0, 12).replace(/\s+/g, "_");
        return (
          <div key={row} className="disease-row" style={{ gridTemplateColumns: "1.4fr 0.9fr 1.1fr 2fr 2fr", alignItems: "center", minWidth: 760 }}>
            <div style={{ fontWeight: 500 }}>{row}</div>
            <div className="freq-cell" style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                <input
                  type="radio"
                  name={`prof_${row}`}
                  checked={(value?.[row]?.consultado || "") === "si"}
                  onChange={() => setCell(row, "consultado", "si")}
                  style={{ accentColor: "var(--sage-500)" }}
                  data-testid={`prof-${safeId}-si`}
                />Sí
              </label>
              <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                <input
                  type="radio"
                  name={`prof_${row}`}
                  checked={(value?.[row]?.consultado || "") === "no"}
                  onChange={() => setCell(row, "consultado", "no")}
                  style={{ accentColor: "var(--sage-500)" }}
                  data-testid={`prof-${safeId}-no`}
                />No
              </label>
            </div>
            <div style={{ padding: "4px 6px" }}>
              <input type="date" className="field-input" value={value?.[row]?.fecha || ""}
                onChange={(e) => setCell(row, "fecha", e.target.value)}
                style={{ padding: "0.4rem 0.55rem", fontSize: "0.85rem" }} />
            </div>
            <div style={{ padding: "4px 6px" }}>
              <input type="text" className="field-input" value={value?.[row]?.centro || ""}
                onChange={(e) => setCell(row, "centro", e.target.value)}
                placeholder="Doctor / centro"
                style={{ padding: "0.4rem 0.55rem", fontSize: "0.85rem" }} />
            </div>
            <div style={{ padding: "4px 6px" }}>
              <input type="text" className="field-input" value={value?.[row]?.resultados || ""}
                onChange={(e) => setCell(row, "resultados", e.target.value)}
                placeholder="Resultados / recomendaciones"
                style={{ padding: "0.4rem 0.55rem", fontSize: "0.85rem" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};


const LegalNoticeField = ({ field }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 rounded-xl overflow-hidden" style={{ background: "var(--sage-50)", border: "1px solid var(--sage-200)" }} data-testid={`field-${field.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-center gap-3 font-sans-body"
        style={{ color: "var(--sage-700, #3d4a25)" }}
        data-testid={`legal-toggle-${field.id}`}
      >
        <ShieldCheck size={18} style={{ color: "var(--sage-600)" }} />
        <span className="flex-1 font-medium text-sm">{field.title}</span>
        <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>
      {open && (
        <div
          className="px-4 pb-4 pt-1 font-sans-body text-xs leading-relaxed whitespace-pre-line"
          style={{ color: "var(--ink-soft)", background: "var(--cream)", borderTop: "1px solid var(--sage-100)" }}
        >
          {field.body}
        </div>
      )}
    </div>
  );
};

const ConsentRequiredField = ({ field, value, onChange }) => {
  const checked = value === true;
  return (
    <label
      className="mt-2 flex items-start gap-3 cursor-pointer rounded-xl p-4"
      style={{ background: checked ? "var(--sage-50)" : "var(--cream)", border: `1px solid ${checked ? "var(--sage-300, #b6c594)" : "var(--sage-100)"}`, transition: "all 0.2s" }}
      data-testid={`field-${field.id}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: "var(--sage-500)", marginTop: "3px", width: 18, height: 18 }}
        data-testid={`consent-${field.id}`}
      />
      <span className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>{field.label}</span>
    </label>
  );
};

const renderField = (field, value, onChange) => {
  const common = { id: field.id, value: value ?? "", onChange: (e) => onChange(e.target.value) };
  if (field.type === "textarea") return <textarea className="field-textarea" rows="3" {...common} data-testid={`field-${field.id}`} />;
  if (field.type === "radio" && Array.isArray(field.options)) {
    return (
      <div className="flex flex-wrap gap-4 mt-2 font-sans-body" data-testid={`field-${field.id}`}>
        {field.options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={field.id}
              checked={value === opt}
              onChange={() => onChange(opt)}
              style={{ accentColor: "var(--sage-500)" }}
              data-testid={`field-${field.id}-${opt}`}
            />
            <span className="text-sm text-[color:var(--ink)]">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "yesno")
    return (
      <div className="flex gap-5 mt-1 font-sans-body" data-testid={`field-${field.id}`}>
        {[["si", "Sí"], ["no", "No"]].map(([v, l]) => (
          <label key={v} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name={field.id} checked={value === v} onChange={() => onChange(v)} style={{ accentColor: "var(--sage-500)" }} data-testid={`field-${field.id}-${v}`} /> {l}
          </label>
        ))}
      </div>
    );
  if (field.type === "frequency") return <FrequencyField field={field} value={value} onChange={onChange} />;
  if (field.type === "frequency_3col") return <Frequency3ColField field={field} value={value} onChange={onChange} />;
  if (field.type === "frequency_4col") return <Frequency4ColField field={field} value={value} onChange={onChange} />;
  if (field.type === "disease_table") return <DiseaseField field={field} value={value} onChange={onChange} />;
  if (field.type === "family_members") return <FamilyMembersField field={field} value={value} onChange={onChange} />;
  if (field.type === "professionals_table") return <ProfessionalsTableField field={field} value={value} onChange={onChange} />;
  if (field.type === "checkbox_list") return <CheckboxListField field={field} value={value} onChange={onChange} />;
  if (field.type === "legal_notice") return <LegalNoticeField field={field} />;
  if (field.type === "consent_required") return <ConsentRequiredField field={field} value={value} onChange={onChange} />;
  return <input type={field.type === "number" ? "number" : field.type} className="field-input" {...common} data-testid={`field-${field.id}`} />;
};

const QuestionnaireFormPage = () => {
  const { qid } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const draftLoaded = useRef(false);
  const DRAFT_KEY = `sv_questionnaire_draft_${qid}`;

  // Cargar cuestionario + detectar borrador previo
  useEffect(() => {
    axios.get(`${API}/questionnaires/${qid}`).then((r) => {
      setQuestionnaire(r.data);
      // Detectar borrador guardado
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && draft.answers && Object.keys(draft.answers).length > 0) {
            setShowResumeBanner(true);
          }
        }
      } catch {
        // Ignoramos errores de parsing
      }
    }).catch(() => toast.error("Cuestionario no encontrado"));
  }, [qid, DRAFT_KEY]);

  // Auto-guardado a localStorage en cada cambio (después de cargar)
  useEffect(() => {
    if (!draftLoaded.current) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        answers, stepIndex, savedAt: new Date().toISOString(),
      }));
      setSavedAt(new Date());
    } catch {
      // Si localStorage está lleno o desactivado, lo ignoramos silenciosamente
    }
  }, [answers, stepIndex, DRAFT_KEY]);

  const resumeDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        setAnswers(draft.answers || {});
        setStepIndex(Math.min(draft.stepIndex || 0, (questionnaire?.sections?.length || 1) - 1));
        toast.success("Continuamos donde lo dejaste ✦");
      }
    } catch {
      toast.error("No se pudo recuperar el borrador");
    }
    setShowResumeBanner(false);
    draftLoaded.current = true;
  };

  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
    setShowResumeBanner(false);
    draftLoaded.current = true;
  };

  // Auto-relleno de población a partir del código postal (API pública zippopotam.us).
  // Se dispara cuando el CP tiene exactamente 5 dígitos.
  useEffect(() => {
    const cp = (answers.codigo_postal || "").trim();
    if (!/^\d{5}$/.test(cp)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`https://api.zippopotam.us/es/${cp}`);
        if (!res.ok) return;
        const data = await res.json();
        const place = data?.places?.[0]?.["place name"];
        if (place && !cancelled) {
          setAnswers((a) => (a.poblacion && a.poblacion.trim() !== "" ? a : { ...a, poblacion: place }));
        }
      } catch { /* offline o CP no encontrado — silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [answers.codigo_postal]);

  if (!questionnaire) return <div className="min-h-[60vh] flex items-center justify-center font-sans-body text-[color:var(--ink-soft)]">Cargando cuestionario...</div>;

  // Si no hay banner que mostrar, activamos el autosave
  if (!showResumeBanner && !draftLoaded.current) {
    draftLoaded.current = true;
  }

  const sections = questionnaire.sections;
  const section = sections[stepIndex];
  const progress = Math.round(((stepIndex + 1) / sections.length) * 100);

  // Calcula edad a partir de la fecha de nacimiento.
  // Devuelve "X años y Y meses" (formato detallado) o número entero según la edad.
  const calcAge = (birthStr, detailed) => {
    if (!birthStr) return "";
    const birth = new Date(birthStr);
    if (Number.isNaN(birth.getTime())) return "";
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    if (today.getDate() < birth.getDate()) months -= 1;
    if (months < 0) { years -= 1; months += 12; }
    if (years < 0) return "";
    if (detailed) {
      if (years === 0) return `${months} ${months === 1 ? "mes" : "meses"}`;
      if (months === 0) return `${years} ${years === 1 ? "año" : "años"}`;
      return `${years} ${years === 1 ? "año" : "años"} y ${months} ${months === 1 ? "mes" : "meses"}`;
    }
    return String(years);
  };

  // Detecta si el cuestionario actual tiene un campo edad y de qué tipo (text vs number)
  // para devolver el formato adecuado al auto-rellenar.
  const findEdadFieldType = () => {
    for (const sec of sections) {
      for (const f of sec.fields || []) {
        if (f.id === "edad") return f.type;
      }
    }
    return null;
  };

  const setAnswer = (fid, v) => setAnswers((a) => {
    const next = { ...a, [fid]: v };
    // Auto-cálculo de edad cuando se modifica la fecha de nacimiento
    if (fid === "fecha_nacimiento") {
      const edadType = findEdadFieldType();
      if (edadType) {
        next.edad = calcAge(v, edadType === "text");
      }
    }
    return next;
  });

  const next = () => {
    // Gating: si la sección tiene gating_field y vale "no", saltar el resto sin validar
    const gating = section.gating_field;
    const gatingValue = gating ? answers[gating] : null;
    const skipRequired = gating && gatingValue === "no";

    if (!skipRequired) {
      for (const f of section.fields) {
        if (!f.required) continue;
        const v = answers[f.id];
        if (f.type === "consent_required") {
          if (v !== true) {
            toast.error("Para enviar el cuestionario debe aceptar la política de protección de datos.");
            return;
          }
        } else if (v === undefined || v === null || v === "") {
          toast.error(`Por favor rellena: ${f.label}`);
          return;
        }
      }
    }
    if (stepIndex < sections.length - 1) {
      setStepIndex(stepIndex + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else submit();
  };

  const back = () => {
    if (stepIndex > 0) { setStepIndex(stepIndex - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  const submit = async () => {
    const name = answers.nombre_nino || answers.nombre || "Paciente";
    const email = answers.email || "";
    if (!email) { toast.error("El email es obligatorio"); return; }
    if (answers.acepta_proteccion_datos !== true) {
      toast.error("Debe aceptar la política de protección de datos para enviar el cuestionario.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/questionnaires/submit`, {
        questionnaire_id: qid, patient_name: name, patient_email: email, answers, website: honeypot,
      });
      // Limpiar borrador tras envío exitoso
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setDone(true);
      toast.success("Cuestionario enviado correctamente");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Error al enviar el cuestionario"); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <main className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="questionnaire-done">
        <div className="soft-card text-center max-w-xl w-full">
          <CheckCircle2 size={54} className="mx-auto" style={{ color: "var(--sage-500)" }} />
          <h1 className="section-title mt-5" style={{ fontSize: "2.2rem" }}>¡Gracias! <span className="handwritten">✦</span></h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-3">
            Hemos recibido tu cuestionario. Nuestro equipo lo revisará antes de tu cita.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-primary">Volver al inicio</Link>
            <button onClick={() => navigate("/cuestionarios")} className="btn-ghost">Ver otros cuestionarios</button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[70vh] py-12 px-6" data-testid="questionnaire-form-page">
      {/* Honeypot anti-spam — invisible para humanos */}
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
        aria-hidden="true"
      />
      <div className="max-w-4xl mx-auto">
        <Link to="/cuestionarios" className="inline-flex items-center gap-1 font-sans-body text-sm text-[color:var(--sage-600)] hover:underline"><ArrowLeft size={15} /> Volver</Link>

        {/* Banner de reanudación de borrador */}
        {showResumeBanner && (
          <div
            className="mt-4 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4"
            style={{ background: "var(--sage-50)", border: "1px solid var(--sage-200)" }}
            data-testid="resume-banner"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}>
                <Save size={18} style={{ color: "var(--sage-600)" }} />
              </div>
              <div className="font-sans-body">
                <div className="font-semibold" style={{ color: "var(--ink)" }}>Tienes un cuestionario a medias</div>
                <div className="text-sm text-[color:var(--ink-soft)] mt-0.5">¿Quieres continuar donde lo dejaste?</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={discardDraft} className="btn-ghost" data-testid="btn-discard-draft" style={{ padding: "0.5rem 1rem" }}>
                <Trash2 size={14} /> Empezar de nuevo
              </button>
              <button onClick={resumeDraft} className="btn-primary" data-testid="btn-resume-draft" style={{ padding: "0.5rem 1.1rem" }}>
                Continuar <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 mb-2">
          <span className="section-eyebrow">{questionnaire.age_range}</span>
          <h1 className="section-title mt-2" style={{ fontSize: "clamp(1.7rem, 3.5vw, 2.5rem)" }}>{questionnaire.title}</h1>
        </div>
        <div className="w-full h-2 rounded-full mt-6" style={{ background: "var(--sage-100)" }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "var(--sage-500)" }} data-testid="progress-bar"></div>
        </div>
        <div className="flex justify-between items-center font-sans-body text-xs text-[color:var(--ink-soft)] mt-2">
          <span>Paso {stepIndex + 1} de {sections.length}</span>
          <div className="flex items-center gap-3">
            {savedAt && (
              <span className="inline-flex items-center gap-1" style={{ color: "var(--sage-600)" }} data-testid="autosave-indicator">
                <CheckCircle2 size={12} /> Guardado automáticamente
              </span>
            )}
            <span>{progress}%</span>
          </div>
        </div>

        <div
          className="soft-card mt-8"
          data-testid={`section-${stepIndex}`}
          style={section.accent_color ? { borderLeft: `5px solid ${section.accent_color}` } : undefined}
        >
          {section.captor && (
            <div className="font-sans-body text-xs uppercase tracking-[0.18em] mb-2" style={{ color: section.accent_color || "var(--sage-600)", fontWeight: 600 }}>
              ✦ Captor {section.captor}
            </div>
          )}
          <h2 className="text-2xl font-medium" style={{ color: section.accent_color || "var(--ink)" }}>{section.title}</h2>
          {section.description && <p className="font-sans-body text-sm text-[color:var(--ink-soft)] mt-2">{section.description}</p>}

          <div className="mt-6 grid md:grid-cols-2 gap-5">
            {section.fields.map((field) => {
              const fullWidth = ["textarea", "frequency", "frequency_3col", "frequency_4col", "disease_table", "family_members", "professionals_table", "checkbox_list", "legal_notice", "consent_required"].includes(field.type);
              return (
                <div key={field.id} className={fullWidth ? "md:col-span-2" : ""}>
                  <label className="field-label">{field.label}{field.required && <span style={{ color: "var(--terracotta)" }}> *</span>}</label>
                  {renderField(field, answers[field.id], (v) => setAnswer(field.id, v))}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap justify-between gap-3 mt-8">
            <button onClick={back} disabled={stepIndex === 0} className="btn-ghost disabled:opacity-40" data-testid="btn-back"><ArrowLeft size={16} /> Anterior</button>
            <button onClick={next} disabled={submitting} className="btn-primary" data-testid="btn-next">
              {stepIndex === sections.length - 1 ? (<>Enviar cuestionario <Send size={16} /></>) : (<>Siguiente <ArrowRight size={16} /></>)}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default QuestionnaireFormPage;
